import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Segmented from "@/components/enhance/Segmented";
import { KEYS } from "@/lib/enhanceApi";
import { COUNTS, HOW, INTERVALS, ROLES, VOICES, usesHarmony } from "@/lib/bgvApi";

function PickRow({ label, value, options, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[13px] text-white/75">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="h-9 w-44 rounded-full border-white/12 bg-black/40 text-[12px] text-white"><SelectValue /></SelectTrigger>
        <SelectContent className="border-white/10 bg-[#0b0e1c] text-white">
          {options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-option-${v.replace(/\s+/g, "-")}`}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

/** How the singers are made, which voices, how many parts, each part's harmony and the key they follow. */
export default function SingersPanel({ doc, settings, change }) {
  const set = (patch) => change((s) => ({ ...s, ...patch }));
  const setPart = (name, interval) => change((s) => ({ ...s, parts: s.parts.map((p) => (p.name === name ? { ...p, interval } : p)) }));
  const harmony = usesHarmony(settings);
  return (
    <section data-testid="bgv-singers" className="space-y-5 rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5 sm:p-6">
      <h2 className="font-display text-base font-semibold md:text-lg">Singers</h2>
      <Segmented label="Made" value={settings.how} options={HOW} onChange={(how) => set({ how })} testId="bgv-how" off={doc.can_choir ? [] : ["choir", "both"]} />
      {harmony ? (
        <>
          <Segmented label="Voices" value={settings.voices} options={VOICES} onChange={(voices) => set({ voices })} testId="bgv-voices" />
          <Segmented label="Harmony" value={settings.count} options={COUNTS} onChange={(count) => set({ count })} testId="bgv-count" />
          <div className="space-y-2.5 rounded-2xl border border-white/[0.06] bg-black/25 p-3.5">
            {settings.parts.slice(0, settings.count).map((p) => (
              <PickRow key={p.name} label={ROLES[p.name]} value={p.interval} options={INTERVALS} onChange={(v) => setPart(p.name, v)} testId={`bgv-interval-${p.name}`} />
            ))}
            <PickRow label={`Key${doc.info?.key ? ` (heard: ${doc.info.key})` : ""}`} value={settings.key || doc.info?.key || "C major"} options={KEYS.map((k) => [k, k])} onChange={(key) => set({ key })} testId="bgv-key" />
          </div>
          <p className="text-[12px] leading-relaxed text-white/45">Harmony lines follow your notes in the key and keep your timing and vibrato. {settings.voices === "choir" ? "Full gospel choir stacks three singers on every part." : "Female parts are your voice reshaped higher and brighter."}</p>
        </>
      ) : <p className="text-[13px] text-white/55">Only the AI choir sings — switch to “From my voice” or “Both” to add harmonies made from your lead.</p>}
    </section>
  );
}
